<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 z-50"
  >
    <div
      class="glass-card dark:text-gray-50 w-full max-w-4xl my-8 relative flex flex-col"
    >
      <!-- Header -->
      <div class="sticky top-0 glass-card z-20 border-b dark:border-gray-700">
        <div class="flex justify-between items-center p-4">
          <h2 class="text-xl font-bold">Export Tunnel CSV</h2>
          <button @click="onClose" class="btn hover:text-red-500">
            <span class="text-3xl">&times;</span>
          </button>
        </div>

        <!-- Select All -->
        <div class="px-4 pb-4">
          <div class="flex items-center space-x-2">
            <input
              type="checkbox"
              id="select-all"
              :checked="allSelected"
              @change="toggleAll"
              class="h-4 w-4 rounded border-gray-300"
            />
            <label for="select-all" class="font-medium"
              >Select All Fields</label
            >
          </div>
        </div>
      </div>

      <!-- Scrollable Content -->
      <div class="flex-1 overflow-y-auto p-4">
        <div class="space-y-6">
          <div
            v-for="(columns, category) in groupedColumns"
            :key="category"
            class="space-y-2"
          >
            <h3 class="font-medium text-gray-700 dark:text-gray-50">{{ category }}</h3>
            <div class="grid grid-cols-2 md:grid-cols-3 gap-2">
              <div
                v-for="column in columns"
                :key="column.key"
                class="flex items-center space-x-2"
              >
                <input
                  type="checkbox"
                  :id="column.key"
                  v-model="selectedColumns"
                  :value="column.key"
                  class="h-4 w-4 rounded border-gray-300"
                />
                <label :for="column.key" class="text-sm">{{
                  column.label
                }}</label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="sticky bottom-0 glass-card z-20 border-t dark:border-gray-700 p-4">
        <div class="flex justify-end space-x-2">
          <button
            @click="onClose"
            class="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 dark:hover:text-gray-900"
          >
            Cancel
          </button>
          <button
            @click="exportCsv"
            :disabled="!selectedColumns.length"
            :class="[
              'btn',
              selectedColumns.length
                ? 'bg-green-600 text-white hover:bg-green-700 dark:bg-green-500'
                : 'bg-gray-300 dark:bg-gray-300 dark:text-gray-50 text-gray-500 cursor-not-allowed',
            ]"
          >
            Download
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from "vue";
import { getTunnelsWithParams } from "@/services/tunnelService";

const availableColumns = [
  // Basic fields
  { key: "name", label: "Name", category: "Basic Info" },
  { key: "description", label: "Description", category: "Basic Info" },
  { key: "tunnel_type", label: "Tunnel Type", category: "Basic Info" },
  { key: "status", label: "Status", category: "Basic Info" },
  
  // Network fields
  { key: "remote_endpoint", label: "Remote Endpoint", category: "Network Info" },
  { key: "local_endpoint", label: "Local Endpoint", category: "Network Info" },
  { key: "port", label: "Port", category: "Network Info" },
  
  // Relationship fields
  { key: "site", label: "Site", category: "Relationships" },
  { key: "org", label: "Organisation", category: "Relationships" },
  
  // Metadata fields
  { key: "created_date", label: "Created Date", category: "Metadata" },
  { key: "last_updated", label: "Last Updated", category: "Metadata" },
  { key: "id", label: "ID", category: "Metadata" }
];

export default {
  name: "TunnelCsvExportDialog",
  props: {
    isOpen: Boolean,
    data: {
      type: Array,
      required: true,
    },
  },
  emits: ["close"],

  setup(props, { emit }) {
    const selectedColumns = ref([]);

    const groupedColumns = computed(() => {
      const groups = {};
      availableColumns.forEach((column) => {
        if (!groups[column.category]) {
          groups[column.category] = [];
        }
        groups[column.category].push(column);
      });
      return groups;
    });

    const allSelected = computed(() => {
      return (
        selectedColumns.value.length === availableColumns.length &&
        availableColumns.every((col) =>
          selectedColumns.value.includes(col.key)
        )
      );
    });

    const toggleAll = () => {
      if (allSelected.value) {
        selectedColumns.value = [];
      } else {
        selectedColumns.value = availableColumns.map((col) => col.key);
      }
    };

    const onClose = () => {
      selectedColumns.value = [];
      emit("close");
    };

    const exportCsv = async () => {
      try {
        // Use the API to get data with selected fields
        const exportData = await getTunnelsWithParams({
          fields: selectedColumns.value.join(","),
        });

        // Create CSV header
        const header = availableColumns
          .filter((col) => selectedColumns.value.includes(col.key))
          .map((col) => col.label)
          .join(",");

        // Create CSV rows
        const rows = exportData.map((item) => {
          return selectedColumns.value
            .map((key) => {
              let value = item[key];
              
              // Format dates
              if (key === "created_date" || key === "last_updated") {
                value = value ? new Date(value).toISOString() : "";
              }
              
              // Handle special cases for relationships
              if (key === "site" || key === "org") {
                value = value?.name || value;
              }
              
              // Escape commas and quotes
              if (typeof value === "string") {
                if (value.includes(",") || value.includes('"')) {
                  value = `"${value.replace(/"/g, '""')}"`;
                }
              }
              
              return value ?? "";
            })
            .join(",");
        });

        // Combine header and rows
        const csv = [header, ...rows].join("\n");

        // Create and trigger download
        const blob = new Blob([csv], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.setAttribute("href", url);
        a.setAttribute("download", "tunnels_export.csv");
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error exporting tunnels:", error);
        // You might want to add error handling UI here
      }
    };

    return {
      selectedColumns,
      groupedColumns,
      allSelected,
      toggleAll,
      onClose,
      exportCsv,
    };
  },
};
</script>
